import { PulseCategory, PulseItem } from "@/models/pulse";

export const CATEGORY_META: Record<PulseCategory, { label: string; tag: string; href: string }> = {
    shorts: { label: "Short interest", tag: "SHORTS", href: "/short-interest" },
    insiders: { label: "Insiders", tag: "INSIDER", href: "/insiders" },
    gov: { label: "Gov trades", tag: "GOV", href: "/us-gov-trades" },
    options: { label: "Options flow", tag: "OPTIONS", href: "/options" },
    alt: { label: "Alt data", tag: "ALT DATA", href: "/alt-data" },
    signals: { label: "Signals", tag: "SIGNAL", href: "/market-intelligence" },
    macro: { label: "Macro", tag: "MACRO", href: "/currencies" },
    news: { label: "News", tag: "NEWS", href: "/news" },
};

export const stockHref = (exchange?: string | null, ticker?: string | null) => (exchange && ticker ? `/s/${exchange}/${ticker}` : null);

export const itemHref = (item: PulseItem) => stockHref(item.exchangeSymbol, item.ticker) || item.link || CATEGORY_META[item.category]?.href || "/";

export const timeAgo = (iso: string | null, now: number = Date.now()) => {
    if (!iso) return "";
    const minutes = Math.round((now - new Date(iso).getTime()) / 60000);
    if (minutes < 1) return "now";
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.round(hours / 24)}d`;
};

// Formatters are built once and reused: toLocaleTimeString / toLocaleString build a new one on every
// call, and the pulse tape alone formats 80 times per render, which made the homepage's first render
// one of its longest tasks on a phone.
let clockFormat: Intl.DateTimeFormat | undefined;

export const clockTime = (iso: string | null) => {
    if (!iso) return "";
    const date = new Date(iso);
    // format() throws on an invalid date, where toLocaleTimeString gave "Invalid Date": show nothing.
    if (Number.isNaN(date.getTime())) return "";
    clockFormat ??= new Intl.DateTimeFormat([], { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
    return clockFormat.format(date);
};

const zoneFormats: Record<string, Intl.DateTimeFormat> = {};
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Weekday (0 = Sunday), hours and minutes of `date` in time zone `timeZone`. */
const zonedParts = (date: Date, timeZone: string) => {
    // formatToParts throws on an invalid date; the old Date round trip gave NaN.
    if (Number.isNaN(date.getTime())) return { day: NaN, hours: NaN, minutes: NaN };
    zoneFormats[timeZone] ??= new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short", hour: "numeric", minute: "numeric", hourCycle: "h23" });
    const parts: Record<string, string> = {};
    for (const part of zoneFormats[timeZone].formatToParts(date)) parts[part.type] = part.value;
    return { day: WEEKDAYS.indexOf(parts.weekday), hours: Number(parts.hour) % 24, minutes: Number(parts.minute) };
};

export const money = (value: number | null | undefined) => {
    if (value === null || value === undefined) return "—";
    const abs = Math.abs(value);
    if (abs >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
    if (abs >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
    if (abs >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
};

export const signed = (value: number | null | undefined, digits = 2, suffix = "") => {
    if (value === null || value === undefined || Number.isNaN(value)) return "—";
    return `${value >= 0 ? "+" : "−"}${Math.abs(value).toFixed(digits)}${suffix}`;
};

export const pct = (value: number | null | undefined, digits = 1) => (value === null || value === undefined ? "—" : `${value.toFixed(digits)}%`);

export type SessionMode = "pre" | "us" | "post";

/** Which part of the trading day it is, in New York time. Drives what the page leads with. */
export const sessionMode = (date: Date = new Date()): { mode: SessionMode; label: string } => {
    const ny = zonedParts(date, "America/New_York");
    const minutes = ny.hours * 60 + ny.minutes;
    const weekend = ny.day === 0 || ny.day === 6;
    if (weekend) return { mode: "post", label: "Markets closed" };
    if (minutes < 9 * 60 + 30) return { mode: "pre", label: minutes >= 3 * 60 ? "Europe session · US pre-market" : "Asia session" };
    if (minutes < 16 * 60) return { mode: "us", label: "US session" };
    return { mode: "post", label: "After the close" };
};

/** A tiny inline SVG sparkline path, scaled to the box. */
export const sparkPath = (values: number[], width: number, height: number, pad = 2) => {
    const clean = values.filter((v) => v !== null && v !== undefined && !Number.isNaN(v));
    if (clean.length < 2) return "";
    const min = Math.min(...clean);
    const max = Math.max(...clean);
    const range = max - min || 1;
    return clean
        .map((v, i) => {
            const x = pad + (i * (width - 2 * pad)) / (clean.length - 1);
            const y = pad + (height - 2 * pad) * (1 - (v - min) / range);
            return `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`;
        })
        .join("");
};

export type Market = { name: string; tz: string; open: number; close: number };

/** Exchange hours in local minutes after midnight (lunch breaks ignored). */
export const MARKETS: Market[] = [
    { name: "Tokyo", tz: "Asia/Tokyo", open: 9 * 60, close: 15 * 60 },
    { name: "London", tz: "Europe/London", open: 8 * 60, close: 16 * 60 + 30 },
    { name: "New York", tz: "America/New_York", open: 9 * 60 + 30, close: 16 * 60 },
];

export const marketClock = (market: Market, now: Date) => {
    const local = zonedParts(now, market.tz);
    const minutes = local.hours * 60 + local.minutes;
    const weekday = local.day > 0 && local.day < 6;
    return {
        time: `${String(local.hours).padStart(2, "0")}:${String(local.minutes).padStart(2, "0")}`,
        open: weekday && minutes >= market.open && minutes < market.close,
    };
};

/**
 * Keep one locked alt-data teaser per list. Below Advanced every alt-data item arrives
 * locked; one says "there is something here, upgrade to see it", a run of them just
 * crowds out data the reader can use.
 */
export const oneLockedTeaser = <T extends { locked?: boolean }>(items: T[]): T[] => {
    let seen = false;
    return items.filter((item) => {
        if (!item.locked) return true;
        if (seen) return false;
        seen = true;
        return true;
    });
};

/**
 * Which lists an Outlier tabs panel loads: those of its own tabs. Macro loads with its tab's
 * content. On wide screens earnings is a panel of its own and not one of the tabs.
 */
export const outlierTabFetches = (tabs: readonly ("earnings" | "alt" | "macro")[]) => ({ earnings: tabs.includes("earnings"), alt: tabs.includes("alt") });

/** The Outlier tab to show: the one picked, while it is still one of `tabs`, else the first. */
export const shownOutlierTab = <T extends "earnings" | "alt" | "macro">(picked: T, tabs: readonly T[]): T => (tabs.includes(picked) ? picked : tabs[0]);

// ---- the lower panels' order -----------------------------------------------------------

/** The data panels whose order follows the day: most unusual first. Also the order before any ranking is known. */
export const RANKED_PANELS = ["shorts", "insiders", "gov", "options"] as const;
export type RankedPanel = (typeof RANKED_PANELS)[number];

/** The day's ranking of the lower panels from the signals feed's top item per dataset; a dataset with nothing today goes last. */
export const rankPanels = (rows: readonly Pick<PulseItem, "category" | "score" | "interest">[]): RankedPanel[] => {
    const interest: Partial<Record<PulseCategory, number>> = {};
    rows.forEach((item) => (interest[item.category] = item.interest ?? item.score));
    return [...RANKED_PANELS].sort((a, b) => (interest[b] ?? -1) - (interest[a] ?? -1));
};

/** A ranking read back from storage, or null unless it names each lower panel exactly once. */
export const parseRanking = (raw: string | null | undefined): RankedPanel[] | null => {
    if (!raw) return null;
    try {
        const value = JSON.parse(raw);
        if (!Array.isArray(value) || value.length !== RANKED_PANELS.length) return null;
        return RANKED_PANELS.every((panel) => value.includes(panel)) ? (value as RankedPanel[]) : null;
    } catch {
        return null;
    }
};

/**
 * The order the lower panels take for as long as they are on screen, decided when they mount:
 * today's ranking when it is already in (phones, where they mount as the reader scrolls near),
 * else the one from the reader's last visit, else the fixed order. Re-ranking panels that are
 * already drawn moved them sideways under the reader, which counted as layout shift.
 */
export const initialRanking = (today: readonly RankedPanel[] | null, lastVisit: readonly RankedPanel[] | null): RankedPanel[] => [...(today ?? lastVisit ?? RANKED_PANELS)];

/**
 * Which of the ranked panels will have rows today, read from the signals feed's one item per
 * dataset (`getPulseSignals({ perCategory: 1 })`, which lists every dataset that has at least one
 * item): a dataset absent there answers an empty list to its own panel's request, and a panel drawn
 * with a skeleton and then taken away when its empty list came back moved its neighbours sideways
 * (ortex-gui#1356). Null while the feed is not in yet, or is still being built (`pending`), so the
 * row waits for it (`lowerRowReady`); every panel when it is in but names no dataset at all, which
 * is the feed's "nothing yet today" rather than "every dataset is quiet".
 */
export const presentPanels = (tops: { rows?: readonly Pick<PulseItem, "category">[]; pending?: boolean } | null | undefined): RankedPanel[] | null => {
    if (!tops || tops.pending) return null;
    const seen = new Set((tops.rows || []).map((row) => row.category));
    if (seen.size === 0) return [...RANKED_PANELS];
    return RANKED_PANELS.filter((panel) => seen.has(panel));
};

/** The ranked panels the lower row holds, and the region they were read for. */
export type LowerRowMembers = { universeId: number; panels: readonly RankedPanel[] };

/**
 * The panels the lower row holds after a render: settled once at mount for the region it mounted
 * in, and read again when the region changes (a signed-in Europe reader's settings arrive after
 * the panels have mounted, or the reader switches region) as soon as the new region's feed is in,
 * so a dataset quiet in one region but not the other is drawn for the other. Until that feed is
 * in, the panels stay as they are; they reload for the new region anyway, and one with nothing
 * there steps aside on its own.
 */
export const nextLowerRowMembers = (members: LowerRowMembers, universeId: number, present: readonly RankedPanel[] | null): LowerRowMembers => (members.universeId !== universeId && present ? { universeId, panels: present } : members);

/** How long the lower row waits for the feed before mounting with every panel, as it always did. */
export const LOWER_ROW_WAIT_MS = 3000;

/**
 * Whether the lower row may mount: once the panels it will hold are known (`presentPanels`), or
 * once `waitedMs` has passed since the page started asking (the feed failed, or is slow): the row
 * then mounts with every panel rather than never.
 */
export const lowerRowReady = (present: readonly RankedPanel[] | null, waitedMs: number): boolean => present !== null || waitedMs >= LOWER_ROW_WAIT_MS;

/** CSS `order` for each lower panel: 1 for the first. */
export const panelOrder = (ranking: readonly RankedPanel[]) => Object.fromEntries(ranking.map((panel, i) => [panel, i + 1])) as Record<RankedPanel, number>;
